import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import type { WarehouseItem } from "./WarehouseLanding.js";

type AdjustReason = "manual_in" | "manual_out" | "restock" | "correction";

interface StockAdjustDialogProps {
  item: WarehouseItem;
  mode: "in" | "out";
  onClose: () => void;
  onSuccess: () => void;
}

const IN_REASONS: { value: AdjustReason; label: string }[] = [
  { value: "manual_in", label: "Ручной приход" },
  { value: "restock", label: "Пополнение запасов" },
  { value: "correction", label: "Коррекция" },
];

const OUT_REASONS: { value: AdjustReason; label: string }[] = [
  { value: "manual_out", label: "Ручной расход" },
  { value: "correction", label: "Коррекция" },
];

export function StockAdjustDialog({
  item,
  mode,
  onClose,
  onSuccess,
}: StockAdjustDialogProps) {
  const [qty, setQty] = useState("1");
  const [reason, setReason] = useState<AdjustReason>(
    mode === "in" ? "manual_in" : "manual_out",
  );
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reasons = mode === "in" ? IN_REASONS : OUT_REASONS;
  const title =
    mode === "in" ? `Приход — ${item.name}` : `Расход — ${item.name}`;
  const submitLabel =
    mode === "in" ? "Записать приход" : "Записать расход";

  const mutation = useMutation({
    mutationFn: ({
      delta,
      reason,
      note,
    }: {
      delta: number;
      reason: AdjustReason;
      note: string;
    }) =>
      api(`/admin/warehouse/items/${item.id}/adjust`, {
        method: "POST",
        body: JSON.stringify({ delta, reason, note }),
      }),
    onSuccess,
    onError: (e: any) => setError(e?.message ?? "Ошибка"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const qtyNum = parseInt(qty, 10);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      setError("Введите положительное количество");
      return;
    }
    if (!note.trim()) {
      setError("Примечание обязательно");
      return;
    }
    const delta = mode === "in" ? qtyNum : -qtyNum;
    mutation.mutate({ delta, reason, note: note.trim() });
  }

  return (
    <div
      className="drawer-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <h2>{title}</h2>
          <div className="grow" />
          <button className="btn-admin" aria-label="Закрыть" onClick={onClose}>
            ✕
          </button>
        </div>
        <form
          className="drawer-body"
          onSubmit={handleSubmit}
          style={{ display: "grid", gap: 16 }}
        >
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Количество ({item.unit})
            </label>
            <input
              className="input-admin"
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              style={{ width: "100%" }}
              aria-label="Количество"
              required
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Причина
            </label>
            <select
              className="input-admin"
              value={reason}
              onChange={(e) => setReason(e.target.value as AdjustReason)}
              style={{ width: "100%" }}
              aria-label="Причина"
            >
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                display: "block",
                marginBottom: 4,
              }}
            >
              Примечание *
            </label>
            <textarea
              className="input-admin"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Обязательно укажите причину операции"
              style={{ width: "100%", minHeight: 80, resize: "vertical" }}
              aria-label="Примечание"
              required
            />
          </div>
          {error && (
            <p style={{ color: "var(--mnp-red, #e53e3e)", fontSize: 13 }}>
              {error}
            </p>
          )}
          <div
            className="f-row"
            style={{ justifyContent: "flex-end", gap: 8 }}
          >
            <button type="button" className="btn-admin" onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              className="btn-admin primary"
              disabled={mutation.isPending}
              aria-label={submitLabel}
            >
              {mutation.isPending ? "Сохранение…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
